import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { httpsCallable } from 'firebase/functions';
import { deleteField, doc, getDoc, serverTimestamp, setDoc } from 'firebase/firestore';
import SubmitButton from '../forms/formComponents/SubmitButton';
import ErrorMessage from '../forms/formComponents/ErrorMessage';
import ParentEmergencySyncDetailsModal from '../forms/ParentEmergencySyncDetailsModal';
import EmergencyContactModal from './EmergencyContactModal';
import { useJoin } from './joinContext';
import { db, functions } from '../../firebase-client';
import { calculateLearnToRowCheckoutPreview } from '../teams/utils/learnToRow';
import { calculateLearnToSailCheckoutPreview } from '../teams/utils/learnToSail';
import { loadPublicLearnToRowCourses } from './learnToRowCourseQueries';
import { loadPublicLearnToSailCourses } from './learnToSailCourseQueries';
import { isCoursePaymentSetupComplete } from './courseSignupSetup';
import { buildRequiredTagGroups, getPricingTagNames } from '../../utils/requiredTags';
import { buildJoinBillingPreview } from './billingPreview';
import { getMembershipHandling, isClubManagedMembershipMode } from '../../utils/membershipHandling';
import { getBespokeModuleSupport } from '../teams/utils/teamMetadata';
import '../../styles/joinMembershipType.css';
const syncEmergencyContactsFromParentFn = httpsCallable(functions, 'syncEmergencyContactsFromParent');
const PRICING_QUESTION_IDS = ['pricingTags', 'pricingTag'];
const isPricingQuestion = q => PRICING_QUESTION_IDS.includes(q?.id);
function normalizeDate(value) {
  if (!value) return null;
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}
function resolveAgeRuleTags(ageRuleTags = [], dob = null) {
  if (!dob || !Array.isArray(ageRuleTags) || ageRuleTags.length === 0) return [];
  const result = new Set();
  for (const ageRuleTag of ageRuleTags) {
    const monthIndex = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'].indexOf(ageRuleTag?.effectiveDate?.month);
    if (monthIndex < 0) continue;
    const refDate = new Date();
    refDate.setMonth(monthIndex);
    refDate.setDate(ageRuleTag?.effectiveDate?.day || 1);
    const ageAtRef = refDate.getFullYear() - dob.getFullYear() - (refDate < new Date(refDate.getFullYear(), dob.getMonth(), dob.getDate()) ? 1 : 0);
    const applies = ageRuleTag?.rule === 'under' ? ageAtRef < Number(ageRuleTag?.age) : ageAtRef >= Number(ageRuleTag?.age);
    if (!applies) continue;
    const ruleTagNames = Array.isArray(ageRuleTag?.tagNames) ? ageRuleTag.tagNames : [ageRuleTag?.tagName];
    ruleTagNames.filter(name => typeof name === 'string' && name.trim() !== '').forEach(name => result.add(name.trim()));
  }
  return Array.from(result);
}
function normalizeTagNames(tags = []) {
  return Array.from(new Set((Array.isArray(tags) ? tags : []).map(tag => typeof tag === 'string' ? tag.trim() : '').filter(Boolean)));
}
function formatProfileName(profile = {}, fallback = 'Member') {
  const first = typeof profile?.firstName === 'string' ? profile.firstName.trim() : '';
  const last = typeof profile?.lastName === 'string' ? profile.lastName.trim() : '';
  const full = `${first} ${last}`.trim();
  return profile?.displayName || profile?.username || full || fallback;
}
function resolveCurrentAccountLabel(profile = {}, fallback = 'Current account') {
  const first = typeof profile?.firstName === 'string' ? profile.firstName.trim() : '';
  if (first) return first;
  const displayName = formatProfileName(profile, '').trim();
  return displayName || fallback;
}
export default function JoinMembershipType() {
  const {
    team,
    user,
    setStatus,
    stripeSetup,
    applyJoinPricingTags,
    error,
    familyMembershipEnabled,
    familyTierOptions,
    membershipPlanType,
    setMembershipPlanType,
    selectedFamilyTierId,
    setSelectedFamilyTierId,
    selectedFamilyTier,
    familyMembershipGroups,
    selectedFamilyId,
    setSelectedFamilyId,
    selectedIndividualTargetFamilyId,
    setSelectedIndividualTargetFamilyId,
    selectedIndividualTargetUserId,
    setSelectedIndividualTargetUserId,
    membershipTargetUserIds,
    setFamilyTargetSelection,
    familySelectionLoading,
    familySelectionError,
    familySelectionValidation,
    questions,
    requiredJoinDocuments,
    membershipPricingTag,
    setMembershipPricingTag,
    cadenceMode,
    cadenceOptions,
    selectedCadence,
    membershipTargetCount,
    preassignedTags,
    waitlistEnabled,
    isPrelaunchCommitteeMode
  } = useJoin();
  const navigate = useNavigate();
  const [familyRoleSelections, setFamilyRoleSelections] = useState({
    parents: [],
    children: []
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const submitLockRef = useRef(false);
  const [emergencyContactModalOpen, setEmergencyContactModalOpen] = useState(false);
  const [syncDetailsModalOpen, setSyncDetailsModalOpen] = useState(false);
  const [individualTargetMode, setIndividualTargetMode] = useState('');
  const [currentAccountLabel, setCurrentAccountLabel] = useState('');
  const [publicLearnToRowCourses, setPublicLearnToRowCourses] = useState([]);
  const [learnToRowJoinPricing, setLearnToRowJoinPricing] = useState({ indicativePriceMajor: '', indicativePriceNote: '' });
  const [publicLearnToSailCourses, setPublicLearnToSailCourses] = useState([]);
  const [learnToSailJoinPricing, setLearnToSailJoinPricing] = useState({ indicativePriceMajor: '', indicativePriceNote: '' });
  const effectiveTeamId = team?.id || '';
  const bespokeModuleSupport = useMemo(
    () => getBespokeModuleSupport(team?.teamMetadata || {}),
    [team?.teamMetadata]
  );
  const learnToRowEnabled = bespokeModuleSupport.rowing && team?.modules?.rowing?.learnToRow?.enabled === true;
  const learnToSailEnabled = bespokeModuleSupport.sailing && team?.modules?.sailing?.learnToSail?.enabled === true;
  const slotValidationSeqRef = useRef(0);
  const latestSlotValidationRef = useRef({});
  const activeFamilyGroup = familyMembershipGroups.find(group => group.familyId === selectedFamilyId) || familyMembershipGroups[0] || null;
  const activeFamilyMembers = useMemo(() => Array.isArray(activeFamilyGroup?.members) ? activeFamilyGroup.members : [], [activeFamilyGroup]);
  const familyParentMembers = useMemo(() => activeFamilyMembers.filter(member => member.role === 'parent'), [activeFamilyMembers]);
  const familyChildMembers = useMemo(() => activeFamilyMembers.filter(member => member.role === 'child'), [activeFamilyMembers]);
  const individualTargetFamilyGroup = useMemo(() => familyMembershipGroups.find(group => group.familyId === selectedIndividualTargetFamilyId) || null, [familyMembershipGroups, selectedIndividualTargetFamilyId]);
  const activeAccountName = currentAccountLabel || 'Current account';
  const activeAccountSummaryLabel = activeAccountName === 'Current account' ? 'the current account' : activeAccountName;
  const individualTargetMembers = useMemo(() => {
    if (familyMembershipGroups.length > 0) {
      return Array.isArray(individualTargetFamilyGroup?.members) ? individualTargetFamilyGroup.members : [];
    }
    if (!user?.uid) return [];
    return [{
      uid: user.uid,
      role: 'member',
      name: activeAccountName
    }];
  }, [activeAccountName, familyMembershipGroups.length, individualTargetFamilyGroup, user?.uid]);
  const individualChildMembers = useMemo(() => individualTargetMembers.filter(member => member.role === 'child'), [individualTargetMembers]);
  const individualTargetSelectionRequired = membershipPlanType === 'individual';
  const individualTargetSelectValue = individualTargetMode === 'me' ? user?.uid || '' : selectedIndividualTargetUserId || '';
  const payerParentName = useMemo(() => {
    const payer = familyParentMembers.find(member => member.uid === user?.uid);
    return payer?.name || 'You (payer)';
  }, [familyParentMembers, user?.uid]);
  const memberNameByUid = useMemo(() => {
    const map = new Map();
    activeFamilyMembers.forEach(member => {
      map.set(member.uid, member.name || 'Family member');
    });
    if (user?.uid && !map.has(user.uid)) map.set(user.uid, activeAccountName);
    return map;
  }, [activeAccountName, activeFamilyMembers, user?.uid]);
  const pricingTagOptions = useMemo(() => {
    const pricingTagNames = [...Object.keys(stripeSetup?.tagOverrides || {}), ...Object.keys(stripeSetup?.tagOverridesYearly || {})].map(name => typeof name === 'string' ? name.trim() : '').filter(Boolean);
    if (pricingTagNames.length === 0) return [];
    const allTags = Array.isArray(team?.tags) ? team.tags : [];
    const tagsByName = new Map(allTags.filter(tag => tag?.type !== 'ageRule' && typeof tag?.name === 'string' && tag.name.trim() !== '').map(tag => [tag.name, tag]));
    return Array.from(new Set(pricingTagNames)).map(tagName => tagsByName.get(tagName) || {
      name: tagName,
      selfAssignable: false
    });
  }, [stripeSetup, team?.tags]);
  const requiredTagGroups = useMemo(() => buildRequiredTagGroups(team?.tags || [], {
    pricingTagNames: getPricingTagNames(stripeSetup || {})
  }), [stripeSetup, team?.tags]);
  const requiredPricingGroups = useMemo(() => requiredTagGroups.filter(group => group.isPricingGroup), [requiredTagGroups]);
  const requiredNonPricingGroups = useMemo(() => requiredTagGroups.filter(group => !group.isPricingGroup), [requiredTagGroups]);
  const requiresPricingTagSelection = membershipPlanType === 'individual' && requiredPricingGroups.length > 0;
  const normalizedPreassignedTags = useMemo(() => normalizeTagNames(preassignedTags), [preassignedTags]);
  const lockedPricingTagName = useMemo(() => {
    if (pricingTagOptions.length === 0 || normalizedPreassignedTags.length === 0) return '';
    const byLowerName = new Map(pricingTagOptions.map(tag => typeof tag?.name === 'string' ? tag.name.trim() : '').filter(Boolean).map(name => [name.toLowerCase(), name]));
    for (const tagName of normalizedPreassignedTags) {
      const match = byLowerName.get(tagName.toLowerCase());
      if (match) return match;
    }
    return '';
  }, [pricingTagOptions, normalizedPreassignedTags]);
  const hasPricingMembershipOptions = pricingTagOptions.length > 0;
  const selectedPricingTagName = useMemo(() => lockedPricingTagName || (typeof membershipPricingTag === 'string' ? membershipPricingTag.trim() : ''), [lockedPricingTagName, membershipPricingTag]);
  const membershipTargetIdsKey = useMemo(() => (Array.isArray(membershipTargetUserIds) ? membershipTargetUserIds.join('|') : ''), [membershipTargetUserIds]);
  const coursePaymentSetupReady = isCoursePaymentSetupComplete(stripeSetup || {});
  const learnToRowWaitlistOpen = Boolean(learnToRowEnabled && team?.modules?.rowing?.learnToRow?.waitlistOpen);
  const learnToRowJoinAvailable = coursePaymentSetupReady && (publicLearnToRowCourses.length > 0 || learnToRowWaitlistOpen);
  const learnToSailWaitlistOpen = Boolean(learnToSailEnabled && team?.modules?.sailing?.learnToSail?.waitlistOpen);
  const learnToSailJoinAvailable = coursePaymentSetupReady && (publicLearnToSailCourses.length > 0 || learnToSailWaitlistOpen);
  const clubManagedMembership = isClubManagedMembershipMode(getMembershipHandling(team || {}).mode);
  const externalPaymentDisplayMode = stripeSetup?.externalPaymentDisplayMode || 'instructions_only';
  const showMembershipPrice = !clubManagedMembership
    || externalPaymentDisplayMode === 'price_only'
    || externalPaymentDisplayMode === 'price_and_instructions';
  const billingPreview = useMemo(() => showMembershipPrice ? buildJoinBillingPreview({
    stripeSetup,
    cadenceMode,
    cadenceOptions,
    selectedCadence,
    showSelectedMemberChoice: false,
    membershipPlanType,
    selectedFamilyTier,
    memberCount: membershipTargetCount,
    appliedTags: [
      ...normalizedPreassignedTags,
      ...(selectedPricingTagName ? [selectedPricingTagName] : [])
    ],
    preferredTag: selectedPricingTagName,
    externalPayment: clubManagedMembership,
  }) : null, [
    cadenceMode,
    cadenceOptions,
    membershipPlanType,
    membershipTargetCount,
    normalizedPreassignedTags,
    selectedCadence,
    selectedFamilyTier,
    selectedPricingTagName,
    showMembershipPrice,
    stripeSetup,
    clubManagedMembership,
  ]);
  const learnToRowIndicativeTotalMinor = useMemo(() => {
    if (publicLearnToRowCourses.length > 0) {
      const totals = publicLearnToRowCourses
        .map((course) => calculateLearnToRowCheckoutPreview({
          courseFeeMinor: Number(course?.courseFeeMinor || 0),
          currency: stripeSetup?.currency || 'GBP',
          teamFeeCfg: stripeSetup || {},
          learnToRowModule: team?.modules?.rowing?.learnToRow || {},
        }))
        .map((preview) => Number(preview?.totalDueMinor || 0))
        .filter((value) => Number.isFinite(value));
      if (totals.length > 0) return Math.min(...totals);
    }
    if (learnToRowJoinPricing.indicativePriceMajor) {
      return Math.max(0, Math.round(Number(learnToRowJoinPricing.indicativePriceMajor || 0) * 100));
    }
    return null;
  }, [learnToRowJoinPricing.indicativePriceMajor, publicLearnToRowCourses, stripeSetup, team?.modules?.rowing?.learnToRow]);
  const learnToSailIndicativeTotalMinor = useMemo(() => {
    if (publicLearnToSailCourses.length > 0) {
      const totals = publicLearnToSailCourses
        .map((course) => calculateLearnToSailCheckoutPreview({
          courseFeeMinor: Number(course?.courseFeeMinor || 0),
          currency: stripeSetup?.currency || 'GBP',
          teamFeeCfg: stripeSetup || {},
          learnToSailModule: team?.modules?.sailing?.learnToSail || {},
        }))
        .map((preview) => Number(preview?.totalDueMinor || 0))
        .filter((value) => Number.isFinite(value));
      if (totals.length > 0) return Math.min(...totals);
    }
    if (learnToSailJoinPricing.indicativePriceMajor) {
      return Math.max(0, Math.round(Number(learnToSailJoinPricing.indicativePriceMajor || 0) * 100));
    }
    return null;
  }, [learnToSailJoinPricing.indicativePriceMajor, publicLearnToSailCourses, stripeSetup, team?.modules?.sailing?.learnToSail]);
  const hasJoinFormQuestions = Array.isArray(questions) && questions.some(q => !isPricingQuestion(q));
  const hasRequiredJoinDocuments = Array.isArray(requiredJoinDocuments) && requiredJoinDocuments.length > 0;
  const requiresDetailsStep = team?.availability?.joinable === 'On Approval' || hasJoinFormQuestions || hasRequiredJoinDocuments || requiredNonPricingGroups.length > 0;
  useEffect(() => {
    let cancelled = false;
    const loadCurrentAccountLabel = async () => {
      if (!user?.uid) {
        if (!cancelled) setCurrentAccountLabel('');
        return;
      }
      const fallbackLabel = user?.displayName?.trim() || user?.email?.trim() || 'Current account';
      try {
        const userSnap = await getDoc(doc(db, 'users', user.uid)).catch(() => null);
        const profile = userSnap?.exists() ? userSnap.data() || {} : {};
        if (!cancelled) {
          setCurrentAccountLabel(resolveCurrentAccountLabel(profile, fallbackLabel));
        }
      } catch {
        if (!cancelled) {
          setCurrentAccountLabel(fallbackLabel);
        }
      }
    };
    loadCurrentAccountLabel();
    return () => {
      cancelled = true;
    };
  }, [user?.displayName, user?.email, user?.uid]);
  useEffect(() => {
    if (!learnToRowEnabled || !effectiveTeamId) {
      setPublicLearnToRowCourses([]);
      return;
    }
    let cancelled = false;
    const loadCourses = async () => {
      try {
        const [courses, learnToRowJoinSnap] = await Promise.all([
          loadPublicLearnToRowCourses(effectiveTeamId),
          getDoc(doc(db, 'teams', effectiveTeamId, 'learnToRowJoinConfig', 'config')).catch(() => null),
        ]);
        if (cancelled) return;
        setPublicLearnToRowCourses(courses);
        setLearnToRowJoinPricing({
          indicativePriceMajor: typeof learnToRowJoinSnap?.data?.()?.indicativePriceMajor === 'string'
            ? learnToRowJoinSnap.data().indicativePriceMajor
            : (Number.isFinite(Number(learnToRowJoinSnap?.data?.()?.indicativePriceMinor))
              ? String((Number(learnToRowJoinSnap.data().indicativePriceMinor || 0) / 100).toFixed(2))
              : ''),
          indicativePriceNote: typeof learnToRowJoinSnap?.data?.()?.indicativePriceNote === 'string'
            ? learnToRowJoinSnap.data().indicativePriceNote
            : '',
        });
      } catch {
        if (!cancelled) {
          setPublicLearnToRowCourses([]);
          setLearnToRowJoinPricing({ indicativePriceMajor: '', indicativePriceNote: '' });
        }
      }
    };
    loadCourses();
    return () => {
      cancelled = true;
    };
  }, [effectiveTeamId, learnToRowEnabled]);
  useEffect(() => {
    if (!learnToSailEnabled || !effectiveTeamId) {
      setPublicLearnToSailCourses([]);
      setLearnToSailJoinPricing({ indicativePriceMajor: '', indicativePriceNote: '' });
      return;
    }
    let cancelled = false;
    const loadCourses = async () => {
      try {
        const [courses, learnToSailJoinSnap] = await Promise.all([
          loadPublicLearnToSailCourses(effectiveTeamId),
          getDoc(doc(db, 'teams', effectiveTeamId, 'learnToSailJoinConfig', 'config')).catch(() => null),
        ]);
        if (cancelled) return;
        setPublicLearnToSailCourses(courses);
        setLearnToSailJoinPricing({
          indicativePriceMajor: typeof learnToSailJoinSnap?.data?.()?.indicativePriceMajor === 'string'
            ? learnToSailJoinSnap.data().indicativePriceMajor
            : (Number.isFinite(Number(learnToSailJoinSnap?.data?.()?.indicativePriceMinor))
              ? String((Number(learnToSailJoinSnap.data().indicativePriceMinor || 0) / 100).toFixed(2))
              : ''),
          indicativePriceNote: typeof learnToSailJoinSnap?.data?.()?.indicativePriceNote === 'string'
            ? learnToSailJoinSnap.data().indicativePriceNote
            : '',
        });
      } catch {
        if (!cancelled) {
          setPublicLearnToSailCourses([]);
          setLearnToSailJoinPricing({ indicativePriceMajor: '', indicativePriceNote: '' });
        }
      }
    };
    loadCourses();
    return () => {
      cancelled = true;
    };
  }, [effectiveTeamId, learnToSailEnabled]);
  useEffect(() => {
    if (lockedPricingTagName) {
      if (membershipPricingTag !== lockedPricingTagName) {
        setMembershipPricingTag(lockedPricingTagName);
      }
      return;
    }
    if (!hasPricingMembershipOptions && membershipPricingTag) {
      setMembershipPricingTag('');
    }
  }, [hasPricingMembershipOptions, lockedPricingTagName, membershipPricingTag, setMembershipPricingTag]);
  useEffect(() => {
    if (!user?.uid) return;

    if (membershipPlanType === 'family_tier') {
      applyJoinPricingTags?.([], {
        memberCount: Math.max(1, (Array.isArray(membershipTargetUserIds) ? membershipTargetUserIds.length : 0) || 1),
        planType: 'family_tier',
        planTierId: selectedFamilyTier?.id || selectedFamilyTierId || null
      });
      return;
    }

    const tagsForPricing = Array.from(new Set([
      ...normalizedPreassignedTags,
      ...(selectedPricingTagName ? [selectedPricingTagName] : [])
    ]));
    applyJoinPricingTags?.(tagsForPricing, {
      memberCount: 1,
      planType: 'individual',
      planTierId: null
    });
  }, [applyJoinPricingTags, membershipPlanType, membershipTargetIdsKey, membershipTargetUserIds, normalizedPreassignedTags, selectedFamilyTier?.id, selectedFamilyTierId, selectedPricingTagName, user?.uid]);
  useEffect(() => {
    if (membershipPlanType === 'individual') return;
    if (individualTargetMode) {
      setIndividualTargetMode('');
    }
  }, [membershipPlanType, individualTargetMode]);
  useEffect(() => {
    if (!individualTargetSelectionRequired) return;
    if (individualTargetMode !== 'family') return;
    const selectedUid = typeof selectedIndividualTargetUserId === 'string' ? selectedIndividualTargetUserId.trim() : '';
    if (!selectedUid) return;
    if (!individualTargetFamilyGroup || !individualTargetMembers.some(member => member.uid === selectedUid)) {
      setSelectedIndividualTargetUserId?.('');
    }
  }, [individualTargetSelectionRequired, individualTargetMode, selectedIndividualTargetUserId, individualTargetFamilyGroup, individualTargetMembers, setSelectedIndividualTargetUserId]);
  useEffect(() => {
    if (!user?.uid || membershipPlanType !== 'family_tier') {
      setFamilyRoleSelections({
        parents: [],
        children: []
      });
      return;
    }
    const parentSlotCount = Math.max(1, Number(selectedFamilyTier?.maxParents || 1));
    const childSlotCount = Math.max(0, Number(selectedFamilyTier?.maxChildren || 0));
    const selectedSet = new Set((Array.isArray(membershipTargetUserIds) ? membershipTargetUserIds : []).map(uid => typeof uid === 'string' ? uid.trim() : '').filter(Boolean));
    const selectedParentIds = familyParentMembers.map(member => member.uid).filter(uid => selectedSet.has(uid) && uid !== user.uid);
    const selectedChildIds = familyChildMembers.map(member => member.uid).filter(uid => selectedSet.has(uid));
    const nextParents = Array(parentSlotCount).fill('');
    nextParents[0] = user.uid;
    selectedParentIds.slice(0, Math.max(0, parentSlotCount - 1)).forEach((uid, index) => {
      nextParents[index + 1] = uid;
    });
    const nextChildren = Array(childSlotCount).fill('');
    selectedChildIds.slice(0, childSlotCount).forEach((uid, index) => {
      nextChildren[index] = uid;
    });
    setFamilyRoleSelections(prev => {
      const parentSame = JSON.stringify(prev.parents) === JSON.stringify(nextParents);
      const childSame = JSON.stringify(prev.children) === JSON.stringify(nextChildren);
      if (parentSame && childSame) return prev;
      return {
        parents: nextParents,
        children: nextChildren
      };
    });
  }, [user?.uid, membershipPlanType, selectedFamilyTier?.maxParents, selectedFamilyTier?.maxChildren, membershipTargetUserIds, familyParentMembers, familyChildMembers]);
  useEffect(() => {
    if (!user?.uid || membershipPlanType !== 'family_tier') return;
    const allSelected = [user.uid, ...(familyRoleSelections.parents || []).slice(1), ...(familyRoleSelections.children || [])].map(uid => typeof uid === 'string' ? uid.trim() : '').filter(Boolean);
    const uniqueSelected = [];
    const seen = new Set();
    allSelected.forEach(uid => {
      if (seen.has(uid)) return;
      seen.add(uid);
      uniqueSelected.push(uid);
    });
    setFamilyTargetSelection?.(uniqueSelected);
  }, [user?.uid, membershipPlanType, familyRoleSelections, setFamilyTargetSelection]);
  const checkOwnEmergencyContacts = async ({
    openModal = true
  } = {}) => {
    if (!user?.uid) return false;
    try {
      const snap = await getDoc(doc(db, 'privateUsers', user.uid));
      const hasContacts = snap.exists() && Array.isArray(snap.data().emergencyContacts) && snap.data().emergencyContacts.length > 0;
      if (!hasContacts && openModal) {
        setEmergencyContactModalOpen(true);
      }
      return hasContacts;
    } catch (err) {
      if (openModal) {
        setSubmitError('Could not check your emergency contact details. Check your connection and try again.');
      }
      return false;
    }
  };
  const updateFamilySlot = (role, index, value) => {
    setFamilyRoleSelections(prev => {
      const next = {
        parents: Array.isArray(prev.parents) ? [...prev.parents] : [],
        children: Array.isArray(prev.children) ? [...prev.children] : []
      };
      if (!Array.isArray(next[role])) return prev;
      next[role][index] = value;
      return next;
    });
  };
  const validateAndApplySlotSelection = async (role, slotIndex, nextUid) => {
    const slotKey = `${role}:${slotIndex}`;
    const uid = typeof nextUid === 'string' ? nextUid.trim() : '';
    if (!uid) {
      latestSlotValidationRef.current[slotKey] = 0;
      updateFamilySlot(role, slotIndex, '');
      return;
    }

    // Optimistically apply immediately so select does not visually "bounce" back.
    updateFamilySlot(role, slotIndex, uid);
    const seq = ++slotValidationSeqRef.current;
    latestSlotValidationRef.current[slotKey] = seq;
    const ok = await ensureMemberEmergencyContacts(uid);

    // Ignore stale async completions when user has changed selection again.
    if (latestSlotValidationRef.current[slotKey] !== seq) return;
    if (!ok) updateFamilySlot(role, slotIndex, '');
  };
  const ensureMemberEmergencyContacts = async (memberUid, familyIdOverride = null) => {
    const uid = typeof memberUid === 'string' ? memberUid.trim() : '';
    if (!uid || !user?.uid) return false;
    if (uid === user.uid) return true;
    const familyIdForCheck = familyIdOverride || activeFamilyGroup?.familyId || selectedFamilyId || null;
    try {
      const checkResult = await syncEmergencyContactsFromParentFn({
        mode: 'check',
        familyId: familyIdForCheck,
        targetUserIds: [uid]
      });
      const checkData = checkResult?.data || {};
      const ineligible = Array.isArray(checkData.ineligibleTargetUids) ? checkData.ineligibleTargetUids : [];
      if (ineligible.includes(uid)) {
        alert('This member is not in your selected family.');
        return false;
      }
      if (
        checkData.missingParentPhone === true
        || checkData.missingParentAddress === true
        || checkData.missingParentContacts === true
      ) {
        setSyncDetailsModalOpen(true);
        return false;
      }
      const missing = Array.isArray(checkData.missingEmergencyContactUserIds) ? checkData.missingEmergencyContactUserIds : [];
      if (!missing.includes(uid)) return true;
      const memberLabel = memberNameByUid.get(uid) || 'This family member';
      const shouldCopy = window.confirm(`${memberLabel} does not include your details as an emergency contact yet. Sync yourself now?`);
      if (!shouldCopy) return false;
      const copyResult = await syncEmergencyContactsFromParentFn({
        mode: 'copy',
        familyId: familyIdForCheck,
        targetUserIds: [uid]
      });
      const copyData = copyResult?.data || {};
      const copied = Array.isArray(copyData.copiedEmergencyContactUserIds) ? copyData.copiedEmergencyContactUserIds : [];
      const already = Array.isArray(copyData.alreadyHasEmergencyContactUserIds) ? copyData.alreadyHasEmergencyContactUserIds : [];
      return copied.includes(uid) || already.includes(uid);
    } catch (copyErr) {
      alert(copyErr?.message || 'Could not verify emergency contacts for this family member.');
      return false;
    }
  };
  const ensureAllSelectedMemberEmergencyContacts = async () => {
    if (membershipPlanType !== 'family_tier' || !user?.uid) return true;
    const nonPayerTargetIds = (Array.isArray(membershipTargetUserIds) ? membershipTargetUserIds : []).map(uid => typeof uid === 'string' ? uid.trim() : '').filter(uid => uid && uid !== user.uid);
    if (nonPayerTargetIds.length === 0) return true;
    try {
      const checkResult = await syncEmergencyContactsFromParentFn({
        mode: 'check',
        familyId: activeFamilyGroup?.familyId || selectedFamilyId || null,
        targetUserIds: nonPayerTargetIds
      });
      const checkData = checkResult?.data || {};
      if (
        checkData.missingParentPhone === true
        || checkData.missingParentAddress === true
        || checkData.missingParentContacts === true
      ) {
        setSyncDetailsModalOpen(true);
        return false;
      }
      const ineligibleIds = Array.isArray(checkData.ineligibleTargetUids) ? checkData.ineligibleTargetUids : [];
      if (ineligibleIds.length > 0) {
        alert('One or more selected members are not in the selected family.');
        return false;
      }
      const missingIds = Array.isArray(checkData.missingEmergencyContactUserIds) ? checkData.missingEmergencyContactUserIds : [];
      if (missingIds.length === 0) return true;
      const missingNames = missingIds.map(uid => memberNameByUid.get(uid) || 'Family member').join(', ');
      const shouldCopy = window.confirm(`${missingNames} ${missingIds.length > 1 ? 'do' : 'does'} not include your details as an emergency contact yet. Sync yourself now?`);
      if (!shouldCopy) return false;
      const copyResult = await syncEmergencyContactsFromParentFn({
        mode: 'copy',
        familyId: activeFamilyGroup?.familyId || selectedFamilyId || null,
        targetUserIds: missingIds
      });
      const copyData = copyResult?.data || {};
      const failedIds = Array.isArray(copyData.failedTargetUids) ? copyData.failedTargetUids : [];
      if (failedIds.length > 0) {
        alert('Could not copy emergency contacts for one or more selected family members.');
        return false;
      }
      return true;
    } catch (copyErr) {
      alert(copyErr?.message || 'Could not verify emergency contacts for selected family members.');
      return false;
    }
  };
  const continueMembership = async () => {
    if (!user?.uid) {
      setSubmitError('Please sign in again to continue setting up your membership.');
      return;
    }
    const hasOwnEmergencyContacts = await checkOwnEmergencyContacts();
    if (!hasOwnEmergencyContacts) return;
    const requiresApproval = !isPrelaunchCommitteeMode && team?.availability?.joinable === 'On Approval';
    const effectiveWaitlistEnabled = !isPrelaunchCommitteeMode && waitlistEnabled;
    if (membershipPlanType === 'family_tier') {
      if (!selectedFamilyTier) {
        alert('Please select a family tier.');
        return;
      }
      if (familyMembershipGroups.length === 0) {
        alert('No linked family members were found for family-tier checkout.');
        return;
      }
      if (familySelectionValidation) {
        alert(familySelectionValidation);
        return;
      }
      const ok = await ensureAllSelectedMemberEmergencyContacts();
      if (!ok) return;
      if (!requiresDetailsStep) {
        try {
          const [payerPrivateSnap, payerPublicSnap] = await Promise.all([
            getDoc(doc(db, 'privateUsers', user.uid)).catch(() => null),
            getDoc(doc(db, 'users', user.uid)).catch(() => null),
          ]);
          const targetDetails = membershipTargetUserIds.map((targetUid) => {
            const familyMember = activeFamilyMembers.find((member) => member.uid === targetUid) || null;
            const appliedTags = targetUid === user.uid ? normalizedPreassignedTags : [];
            return {
              uid: targetUid,
              displayName: familyMember?.name || memberNameByUid.get(targetUid) || 'Family member',
              role: familyMember?.role || (targetUid === user.uid ? 'parent' : null),
              isPayer: targetUid === user.uid,
              formResponses: [],
              appliedTags,
              requestedTags: [],
            };
          });
          await setDoc(doc(db, 'teams', team.id, 'pendingMembers', user.uid), {
            uid: user.uid,
            displayName: payerPublicSnap?.exists() ? payerPublicSnap.data().username || '' : '',
            email: payerPrivateSnap?.exists() ? payerPrivateSnap.data().email || '' : '',
            phone: payerPrivateSnap?.exists() ? payerPrivateSnap.data().phone || '' : '',
            firstName: payerPublicSnap?.exists() ? payerPublicSnap.data().firstName || '' : '',
            lastName: payerPublicSnap?.exists() ? payerPublicSnap.data().lastName || '' : '',
            dob: payerPublicSnap?.exists() ? payerPublicSnap.data().dob || '' : '',
            formResponses: [],
            appliedTags: normalizedPreassignedTags,
            requestedTags: [],
            membershipPricingTag: '',
            preassignedTags: normalizedPreassignedTags,
            membershipTargetDetails: targetDetails,
            membershipPlanType: 'family_tier',
            membershipPlanTierId: selectedFamilyTier?.id || selectedFamilyTierId || null,
            membershipTargetUserIds,
            membershipTargetFamilyId: activeFamilyGroup?.familyId || selectedFamilyId || null,
            status: effectiveWaitlistEnabled ? 'waitlisted' : 'approved',
            approvedAt: serverTimestamp(),
            waitlistPriority: null,
            waitlistedAt: effectiveWaitlistEnabled ? serverTimestamp() : null,
            releasedFromWaitlistAt: null,
            createdAt: serverTimestamp(),
          }, {
            merge: true,
          });
          setStatus(effectiveWaitlistEnabled ? 'waitlisted' : 'payment');
          return;
        } catch (err) {
          setSubmitError('Could not save your membership selection. Check your connection and try again.');
          return;
        }
      }
    }
    if (membershipPlanType === 'individual') {
      if (individualTargetSelectionRequired && !individualTargetMode) {
        alert('Please choose whether this membership is for you or someone in your family.');
        return;
      }
      const individualTargetUid = individualTargetMode === 'family'
        ? (typeof selectedIndividualTargetUserId === 'string' ? selectedIndividualTargetUserId.trim() : '')
        : user.uid;
      if (!individualTargetUid) {
        alert('Please choose which account this membership is for.');
        return;
      }
      const individualTargetFamilyId = individualTargetSelectionRequired && individualTargetMode === 'family' ? selectedIndividualTargetFamilyId || '' : '';
      if (individualTargetSelectionRequired && individualTargetMode === 'family' && !individualTargetFamilyId) {
        alert('Please choose which family to pick the account from.');
        return;
      }
      if (individualTargetUid !== user.uid) {
        const ok = await ensureMemberEmergencyContacts(individualTargetUid, individualTargetFamilyId);
        if (!ok) return;
      }
      const allTags = Array.isArray(team?.tags) ? team.tags : [];
      const ageRuleTags = allTags.filter(tag => tag?.type === 'ageRule');
      const selectedTagName = lockedPricingTagName || (typeof membershipPricingTag === 'string' ? membershipPricingTag.trim() : '');
      const selectedTagMeta = selectedTagName ? pricingTagOptions.find(tag => tag.name === selectedTagName) || null : null;
      if (requiresPricingTagSelection && !selectedTagName) {
        alert(`Please choose a required tag in ${requiredPricingGroups[0].category}.`);
        return;
      }
      if (selectedTagName && !selectedTagMeta) {
        alert('Please pick a valid membership pricing option.');
        return;
      }
      const [payerPrivateSnap, payerPublicSnap, targetPrivateSnap, targetPublicSnap] = await Promise.all([
        getDoc(doc(db, 'privateUsers', user.uid)).catch(() => null),
        getDoc(doc(db, 'users', user.uid)).catch(() => null),
        getDoc(doc(db, 'privateUsers', individualTargetUid)).catch(() => null),
        getDoc(doc(db, 'users', individualTargetUid)).catch(() => null)
      ]);
      const targetPublicData = targetPublicSnap?.exists() ? targetPublicSnap.data() || {} : {};
      const targetPrivateData = targetPrivateSnap?.exists() ? targetPrivateSnap.data() || {} : {};
      const targetFamilyMember = individualTargetMembers.find(member => member.uid === individualTargetUid) || null;
      const targetDisplayName = targetFamilyMember?.name || formatProfileName(targetPublicData, individualTargetUid === user.uid ? 'You' : 'Family member');
      const dobRaw = targetPrivateData?.dob || targetPublicData?.dob || null;
      const dob = normalizeDate(dobRaw);
      const appliedSet = new Set([...(individualTargetUid === user.uid ? normalizedPreassignedTags : []), ...resolveAgeRuleTags(ageRuleTags, dob)]);
      const requestedTags = [];
      if (selectedTagMeta?.name) {
        if (lockedPricingTagName && selectedTagMeta.name === lockedPricingTagName) {
          appliedSet.add(selectedTagMeta.name);
        } else {
          appliedSet.add(selectedTagMeta.name);
        }
      }
      const appliedTags = Array.from(appliedSet);
      const hasRequestedTags = requestedTags.length > 0;
      applyJoinPricingTags?.(appliedTags, {
        memberCount: 1,
        planType: 'individual',
        planTierId: null
      });
      if (!requiresDetailsStep) {
        const shouldWaitForApproval = requiresApproval || hasRequestedTags;
        try {
          const nextStatus = shouldWaitForApproval
            ? 'waiting'
            : effectiveWaitlistEnabled
              ? 'waitlisted'
              : 'payment';
          const formData = {
            uid: user.uid,
            displayName: payerPublicSnap?.exists() ? payerPublicSnap.data().username || '' : '',
            email: payerPrivateSnap?.exists() ? payerPrivateSnap.data().email || '' : '',
            phone: payerPrivateSnap?.exists() ? payerPrivateSnap.data().phone || '' : '',
            firstName: payerPublicSnap?.exists() ? payerPublicSnap.data().firstName || '' : '',
            lastName: payerPublicSnap?.exists() ? payerPublicSnap.data().lastName || '' : '',
            dob: payerPublicSnap?.exists() ? payerPublicSnap.data().dob || '' : '',
            formResponses: [],
            appliedTags,
            requestedTags,
            membershipPricingTag: selectedTagMeta?.name || lockedPricingTagName || '',
            preassignedTags: normalizedPreassignedTags,
            membershipTargetDetails: [{
              uid: individualTargetUid,
              displayName: targetDisplayName,
              role: targetFamilyMember?.role || (individualTargetUid === user.uid ? 'parent' : null),
              isPayer: individualTargetUid === user.uid,
              formResponses: [],
              appliedTags,
              requestedTags
            }],
            membershipPlanType: 'individual',
            membershipPlanTierId: null,
            membershipTargetUserIds: [individualTargetUid],
            membershipTargetFamilyId: individualTargetUid === user.uid ? null : individualTargetFamilyId || null,
            status: nextStatus === 'payment' ? 'approved' : nextStatus,
            approvedAt: nextStatus === 'payment' || nextStatus === 'waitlisted' ? serverTimestamp() : deleteField(),
            waitlistPriority: null,
            waitlistedAt: nextStatus === 'waitlisted' ? serverTimestamp() : null,
            releasedFromWaitlistAt: null,
            createdAt: serverTimestamp()
          };
          await setDoc(doc(db, 'teams', team.id, 'pendingMembers', user.uid), formData, {
            merge: true
          });
          setStatus(nextStatus);
          return;
        } catch (err) {
          setSubmitError('Could not save your membership selection. Check your connection and try again.');
          return;
        }
      }
    }
    setStatus(requiresDetailsStep ? 'form' : 'payment');
  };
  const handleContinue = async e => {
    e.preventDefault();
    if (submitLockRef.current) return;
    submitLockRef.current = true;
    setSubmitting(true);
    setSubmitError('');
    try {
      await continueMembership();
    } catch (err) {
      setSubmitError('Could not continue membership setup. Please try again.');
    } finally {
      submitLockRef.current = false;
      setSubmitting(false);
    }
  };
  return <>
    <form onSubmit={handleContinue} className="form-stacked space-y-4 text-left">
      <div>
        <h2 className="join-flow-section-title">Membership options</h2>
        <p className="join-flow-section-copy">
          Choose the membership and account the club should attach it to. The summary updates as you make changes.
        </p>
      </div>

      <div className="form-group">
        <label>Choose Membership Type</label>
        <div className="join-plan-grid">
          <label className={`join-plan-card ${membershipPlanType !== 'family_tier' ? 'is-selected' : ''}`}>
            <span className="join-plan-card__header">
            <input type="radio" name="membershipPlanType" checked={membershipPlanType !== 'family_tier'} onChange={() => setMembershipPlanType?.('individual')} />
              <span>
                <span className="join-plan-card__title">Individual Membership</span>
                <span className="join-plan-card__meta">Just your own membership.</span>
                <span className="join-plan-card__meta">
                  {hasPricingMembershipOptions ? 'Choose who this membership is for and any pricing option below.' : 'Choose who this membership is for below.'}
                </span>
              </span>
            </span>
          </label>
          {familyTierOptions.map(tier => <label key={tier.id} className={`join-plan-card ${membershipPlanType === 'family_tier' && (selectedFamilyTierId || selectedFamilyTier?.id) === tier.id ? 'is-selected' : ''}`}>
              <span className="join-plan-card__header">
              <input type="radio" name="membershipPlanType" checked={membershipPlanType === 'family_tier' && (selectedFamilyTierId || selectedFamilyTier?.id) === tier.id} onChange={() => {
              setMembershipPlanType?.('family_tier');
              setSelectedFamilyTierId?.(tier.id);
              setMembershipPricingTag?.('');
            }} />
              <span>
                  <span className="join-plan-card__title">{tier.label}</span>
                  <span className="join-plan-card__meta">
                  Up to {tier.maxParents} parent(s) and {tier.maxChildren} child(ren){tier.childMaxAge ? ` under ${tier.childMaxAge}` : ''}
                  </span>
              </span>
              </span>
            </label>)}
          {learnToRowJoinAvailable && <button type="button" className="join-plan-card" onClick={() => {
            const selectedCourseId = publicLearnToRowCourses.length === 1 ? publicLearnToRowCourses[0].id : '';
            navigate(selectedCourseId ? `/join/${effectiveTeamId}?joinMode=learn-to-row&courseId=${selectedCourseId}` : `/join/${effectiveTeamId}?joinMode=learn-to-row`);
          }}>
              <span className="join-plan-card__header">
                <span>
                  <span className="join-plan-card__title">Learn to Row</span>
                  <span className="join-plan-card__meta">
                    Beginner course signup with course dates, capped places, and a club-managed Learn to Row waitlist.
                  </span>
                  {learnToRowIndicativeTotalMinor != null ? <span className="join-plan-card__meta">
                      {publicLearnToRowCourses.length > 0 ? 'From ' : 'Indicative price '}
                      {new Intl.NumberFormat('en-GB', {
                    style: 'currency',
                    currency: String(stripeSetup?.currency || 'GBP').toUpperCase()
                  }).format(learnToRowIndicativeTotalMinor / 100)}
                      {publicLearnToRowCourses.length > 0 ? ' estimated total' : ''}
                    </span> : null}
                  {learnToRowJoinPricing.indicativePriceNote ? <span className="join-plan-card__meta">{learnToRowJoinPricing.indicativePriceNote}</span> : null}
                </span>
              </span>
            </button>}
          {learnToSailJoinAvailable && <button type="button" className="join-plan-card" onClick={() => {
            const selectedCourseId = publicLearnToSailCourses.length === 1 ? publicLearnToSailCourses[0].id : '';
            navigate(selectedCourseId ? `/join/${effectiveTeamId}?joinMode=learn-to-sail&courseId=${selectedCourseId}` : `/join/${effectiveTeamId}?joinMode=learn-to-sail`);
          }}>
              <span className="join-plan-card__header">
                <span>
                  <span className="join-plan-card__title">Learn to Sail</span>
                  <span className="join-plan-card__meta">
                    Beginner course signup with course dates, capped places, and a club-managed Learn to Sail waitlist.
                  </span>
                  {learnToSailIndicativeTotalMinor != null ? <span className="join-plan-card__meta">
                      {publicLearnToSailCourses.length > 0 ? 'From ' : 'Indicative price '}
                      {new Intl.NumberFormat('en-GB', {
                    style: 'currency',
                    currency: String(stripeSetup?.currency || 'GBP').toUpperCase()
                  }).format(learnToSailIndicativeTotalMinor / 100)}
                      {publicLearnToSailCourses.length > 0 ? ' estimated total' : ''}
                    </span> : null}
                  {learnToSailJoinPricing.indicativePriceNote ? <span className="join-plan-card__meta">{learnToSailJoinPricing.indicativePriceNote}</span> : null}
                </span>
              </span>
            </button>}
        </div>
      </div>

      {membershipPlanType === 'individual' && <div className="form-group join-family-coverage">
          <label>Who Is This Membership For?</label>
          <p className="text-muted" style={{
        marginTop: '0.25rem'
      }}>
            Current account: <strong>{activeAccountName}</strong>. Choose this account if the membership should follow the profile you&apos;re using now, or pick someone else in your family.
          </p>
          <div className="form-group" style={{
        marginTop: '0.75rem'
      }}>
              <label>Select Account</label>
              <select className="form-input" value={individualTargetMode} onChange={e => {
          const nextMode = e.target.value;
          setIndividualTargetMode(nextMode);
          if (nextMode === 'me') {
            setSelectedIndividualTargetFamilyId?.('');
            setSelectedIndividualTargetUserId?.(user?.uid || '');
          } else {
            setSelectedIndividualTargetFamilyId?.('');
            setSelectedIndividualTargetUserId?.('');
          }
        }} required>
                <option value="">Select option</option>
                <option value="me">{activeAccountName} (current account)</option>
                <option value="family">Someone in my family</option>
              </select>
            </div>
          {familyMembershipGroups.length > 0 && individualTargetMode === 'family' && <div className="form-group" style={{
        marginTop: '0.75rem'
      }}>
              <label>Select Family</label>
              <select className="form-input" value={selectedIndividualTargetFamilyId || ''} onChange={e => {
          setSelectedIndividualTargetFamilyId?.(e.target.value);
          setSelectedIndividualTargetUserId?.('');
        }} required>
                <option value="">Select family</option>
                {familyMembershipGroups.map(group => <option key={group.familyId} value={group.familyId}>
                    {group.familyName}
                  </option>)}
              </select>
            </div>}
          {individualTargetMode === 'me' && <p className="text-muted" style={{
        marginTop: '0.5rem'
      }}>
              This membership will be attached to {activeAccountSummaryLabel}.
            </p>}
          {individualTargetMode === 'family' && familyMembershipGroups.length > 0 && <select className="form-input" style={{
        marginTop: '0.5rem'
      }} value={individualTargetSelectValue} onChange={e => setSelectedIndividualTargetUserId?.(e.target.value)} disabled={!selectedIndividualTargetFamilyId} required>
            <option value="">Select account</option>
            {individualTargetMembers.map(member => <option key={member.uid} value={member.uid}>
                {member.uid === user?.uid ? `${member.name || 'You'} (you)` : member.name || 'Family member'}{member.role ? ` - ${member.role}` : ''}
              </option>)}
          </select>}
          <p className="text-muted" style={{
        marginTop: '0.5rem'
      }}>
            {familyMembershipGroups.length > 0 ? 'Buying for a child? Choose someone in your family, then select their child account so the club membership is attached to them.' : 'Buying for a child or another family member? Set up family accounts first so you can choose them here.'}
          </p>
          {(familyMembershipGroups.length === 0 || (individualTargetMode === 'family' && selectedIndividualTargetFamilyId && individualChildMembers.length === 0)) && <div style={{
        marginTop: '0.5rem'
      }}>
              <p className="text-muted" style={{
          marginBottom: '0.4rem'
        }}>
                Need to buy for a child? Add their child account first.
              </p>
              <button type="button" className="button button-secondary" onClick={() => navigate('/profile?tab=family')}>
                Set Up Child Account
              </button>
            </div>}
        </div>}

      {normalizedPreassignedTags.length > 0 && <div className="form-group join-family-coverage">
          <label>Admin-Assigned Tags</label>
          <p className="text-muted" style={{
        marginTop: '0.25rem'
      }}>
            These tags were pre-assigned by your club and will be applied automatically.
          </p>
          <p style={{
        marginTop: '0.4rem'
      }}>{normalizedPreassignedTags.join(', ')}</p>
        </div>}

      {membershipPlanType === 'individual' && hasPricingMembershipOptions && <div className="form-group join-family-coverage">
          <label>Membership Pricing Option</label>
          <p className="text-muted" style={{
        marginTop: '0.25rem'
      }}>
            {lockedPricingTagName ? 'This option was pre-assigned by your club.' : requiresPricingTagSelection ? 'Pick the required pricing option that applies to the selected account.' : 'Pick the pricing option that applies to the selected account.'}
          </p>
          <select className="form-input" style={{
        marginTop: '0.5rem'
      }} value={lockedPricingTagName || membershipPricingTag || ''} onChange={e => setMembershipPricingTag?.(e.target.value)} disabled={Boolean(lockedPricingTagName)} required={requiresPricingTagSelection}>
            {!requiresPricingTagSelection && <option value="">Default pricing</option>}
            {requiresPricingTagSelection && <option value="">Select required option</option>}
            {pricingTagOptions.map(tag => <option key={tag.name} value={tag.name}>
                {tag.name}{tag.selfAssignable ? '' : ' (requires approval)'}
              </option>)}
          </select>
        </div>}

      {familyMembershipEnabled && membershipPlanType === 'family_tier' && <div className="form-group join-family-coverage">
          <label>Family Coverage</label>
          <p className="text-muted" style={{
        marginTop: '0.25rem'
      }}>
            {selectedFamilyTier ? `${selectedFamilyTier.label} includes up to ${selectedFamilyTier.maxParents} parent(s) and ${selectedFamilyTier.maxChildren} child(ren).` : 'Select family members included in this tier.'}
          </p>
          <div style={{
        marginTop: '0.5rem'
      }}>
            <button type="button" className="button button-secondary" onClick={() => navigate('/profile?tab=family')}>
              Edit Family In Profile
            </button>
          </div>

          {familySelectionLoading && <p className="text-muted" style={{
        marginTop: '0.5rem'
      }}>Loading family members…</p>}
          {familySelectionError && <p className="text-error" style={{
        marginTop: '0.5rem'
      }}>{familySelectionError}</p>}
          {!familySelectionLoading && !familySelectionError && familyMembershipGroups.length === 0 && <p className="text-muted" style={{
        marginTop: '0.5rem'
      }}>
              No linked family members were found for family-tier checkout.
            </p>}

          {!familySelectionLoading && familyMembershipGroups.length > 0 && <>
              {familyMembershipGroups.length > 1 && <div className="form-group" style={{
          marginTop: '0.75rem'
        }}>
                  <label>Select Family</label>
                  <select className="form-input" value={activeFamilyGroup?.familyId || ''} onChange={e => setSelectedFamilyId?.(e.target.value)}>
                    {familyMembershipGroups.map(group => <option key={group.familyId} value={group.familyId}>
                        {group.familyName}
                      </option>)}
                  </select>
                </div>}

              {activeFamilyGroup?.members?.length > 0 && <div className="join-family-grid">
                  <p className="text-muted" style={{
            marginTop: 0
          }}>
                    Parent 1 is always you as payer. Add any extra parents/children you want covered.
                  </p>

                  <div style={{
            display: 'grid',
            gap: '0.45rem'
          }}>
                    <strong style={{
              fontSize: '0.95rem'
            }}>Parents</strong>
                    {Array.from({
              length: Math.max(1, Number(selectedFamilyTier?.maxParents || 1))
            }).map((_, slotIndex) => <div key={`parent-slot-${slotIndex}`} className="form-group" style={{
              marginBottom: 0
            }}>
                        <label>{`Parent ${slotIndex + 1}`}</label>
                        <select className="form-input" value={slotIndex === 0 ? user?.uid || '' : familyRoleSelections.parents?.[slotIndex] || ''} disabled={slotIndex === 0} onChange={e => {
                validateAndApplySlotSelection('parents', slotIndex, e.target.value);
              }}>
                          {slotIndex === 0 ? <option value={user?.uid || ''}>{payerParentName}</option> : <>
                              <option value="">Not included</option>
                              {familyParentMembers.filter(member => member.uid !== user?.uid).map(member => {
                    const inOtherParentSlot = (familyRoleSelections.parents || []).some((selectedUid, selectedIndex) => selectedIndex !== slotIndex && selectedUid === member.uid);
                    return <option key={member.uid} value={member.uid} disabled={inOtherParentSlot}>
                                      {member.name}
                                    </option>;
                  })}
                            </>}
                        </select>
                      </div>)}
                  </div>

                  <div style={{
            display: 'grid',
            gap: '0.45rem'
          }}>
                    {Number(selectedFamilyTier?.maxChildren || 0) > 0 && <strong style={{
              fontSize: '0.95rem'
            }}>Children</strong>}
                    {Array.from({
              length: Math.max(0, Number(selectedFamilyTier?.maxChildren || 0))
            }).map((_, slotIndex) => <div key={`child-slot-${slotIndex}`} className="form-group" style={{
              marginBottom: 0
            }}>
                        <label>{`Child ${slotIndex + 1}`}</label>
                        <select className="form-input" value={familyRoleSelections.children?.[slotIndex] || ''} onChange={e => {
                validateAndApplySlotSelection('children', slotIndex, e.target.value);
              }}>
                          <option value="">Not included</option>
                          {familyChildMembers.map(member => {
                  const inOtherChildSlot = (familyRoleSelections.children || []).some((selectedUid, selectedIndex) => selectedIndex !== slotIndex && selectedUid === member.uid);
                  return <option key={member.uid} value={member.uid} disabled={inOtherChildSlot}>
                                {member.name}
                              </option>;
                })}
                        </select>
                      </div>)}
                  </div>
                </div>}
            </>}

          {familySelectionValidation && <p className="text-error" style={{
        marginTop: '0.5rem'
      }}>{familySelectionValidation}</p>}
        </div>}

      {billingPreview && <div className="form-group join-family-coverage join-billing-preview">
          <label>Billing options</label>
          <p className="text-muted" style={{
        marginTop: '0.25rem'
      }}>
            {billingPreview.description}
          </p>
          <ul className="join-billing-preview__list">
            {billingPreview.lines.map(line => <li key={line}>{line}</li>)}
          </ul>
          {billingPreview.note && <p className="text-muted join-billing-preview__note">
              {billingPreview.note}
            </p>}
        </div>}

      {error && <ErrorMessage message={error} />}
      {submitError && <ErrorMessage message={submitError} />}

      <div className="form-actions flex justify-end">
        <SubmitButton label={submitting ? 'Continuing…' : 'Continue'} primary disabled={submitting || (membershipPlanType === 'family_tier' && familySelectionLoading) || (individualTargetSelectionRequired && (!individualTargetMode || (individualTargetMode === 'family' && (!selectedIndividualTargetFamilyId || !selectedIndividualTargetUserId))))} />
      </div>
    </form>
    {emergencyContactModalOpen && user?.uid && <EmergencyContactModal userId={user.uid} onClose={() => setEmergencyContactModalOpen(false)} onSaved={async () => {
      const hasContacts = await checkOwnEmergencyContacts({
        openModal: false
      });
      if (hasContacts) {
        setEmergencyContactModalOpen(false);
        await handleContinue({ preventDefault() {} });
      }
    }} />}
    {syncDetailsModalOpen && user?.uid && <ParentEmergencySyncDetailsModal userId={user.uid} onClose={() => setSyncDetailsModalOpen(false)} onSaved={() => {
      setSyncDetailsModalOpen(false);
    }} />}
  </>;
}
